import { createSelector } from '@reduxjs/toolkit';
import {
  fetchAllIdentifiersRequest,
  fetchIdentifierCustomersRequest,
  fetchIdentifierTypesRequest,
  fetchRelationshipTypesRequest,
  fetchRenderedPromptsRequest,
  fetchSingleIdentifierRequest,
  generateTermsRequest,
  saveIdentifierRequest,
} from 'actions/identifier';
import { mapInputChoices, possibleTypes } from 'constants/annotator';
import { isEqual } from 'lodash';
import { GlobalState } from 'reducers';
import type {
  AnnotatorTypes,
  Customer,
  Identifier,
  LanguageMatcherFieldSpec,
  LanguageMatcherForm,
  LanguageMatcherType,
  LanguageMatchersTypes,
  MLanguageMatcher,
  NormalizedIdentifier,
  NormalizedRelationshipType,
  NormalizedResource,
  Selector,
  UUID,
} from 'types';
import { UtilizationType } from './config';

export const getFetchAllIdentifiersLoading: Selector<boolean> = (state) =>
  state.identifier.loading.includes(fetchAllIdentifiersRequest.toString());

export const getFetchIdentifiersTypesLoading: Selector<boolean> = (state) =>
  state.identifier.loading.includes(fetchIdentifierTypesRequest.toString());

export const saveIdentifierRequestLoading: Selector<boolean> = (state) =>
  state.identifier.loading.includes(saveIdentifierRequest.toString());

export const getIdentifiers: Selector<NormalizedIdentifier[]> = createSelector(
  [(state: GlobalState): NormalizedResource<NormalizedIdentifier> => state.identifier.items],
  (identifiers) => Object.values(identifiers)
);

export const getIdentifiersForFilterPills: Selector<NormalizedIdentifier[]> = createSelector(
  [(state: GlobalState): NormalizedResource<NormalizedIdentifier> => state.identifier.items],
  (filterItems) => Object.keys(filterItems).map((uuid) => filterItems[uuid])
);

export const getIdentifierTypes: Selector<AnnotatorTypes> = (state) => state.identifier.types;
export const getIdentifierTypesRequestLoading: Selector<boolean> = (state) =>
  state.identifier.loading.includes(fetchIdentifierTypesRequest.toString());

export const getIdentifier: Selector<NormalizedIdentifier, [UUID]> = (state, id) =>
  state.identifier.items[id];

export const getSelectedIdentifier: Selector<Identifier | null> = (state) => {
  let { identifier } = state.identifier.stagingIdentifier;
  if (state.identifier.selectedCustomerGroup === 'production') {
    identifier = state.identifier.productionIdentifier.identifier;
  }
  return identifier;
};

export const getSelectedIdentifierRevision: Selector<NormalizedIdentifier | null> = (state) => {
  let revision = state.identifier.stagingIdentifier.identifierRevision;
  if (state.identifier.selectedCustomerGroup === 'production') {
    revision = state.identifier.productionIdentifier.identifierRevision;
  }
  return revision;
};

export const getIdentifierCustomers: Selector<Customer[]> = (state) => {
  let customers = state.identifier.stagingIdentifier.identifierCustomers;
  if (state.identifier.selectedCustomerGroup === 'production') {
    customers = state.identifier.productionIdentifier.identifierCustomers;
  }
  return customers;
};

export const getIdentifierRequestLoading: Selector<boolean> = (state) =>
  state.identifier.loading.includes(fetchSingleIdentifierRequest.toString());

export const unsavedChanges: Selector<boolean> = (state) => state.identifier.unsavedChanges;

export const getSelectedCustomerIdentifier: Selector<string> = (state) =>
  state.identifier.selectedCustomer;

export const filterLanguageMatchers = (
  languageMatchers: MLanguageMatcher[],
  languageMatcherFilter: string
): MLanguageMatcher[] => {
  if (languageMatcherFilter === '') {
    return languageMatchers;
  }
  return languageMatchers.filter((el) => {
    const responseArr = Object.values(el).map((value) => {
      if (Array.isArray(value)) {
        const response = value.map(
          (item) => item.toUpperCase().indexOf(languageMatcherFilter) > -1
        );
        if (response.some((val) => val)) return true;
        return false;
      }
      if (typeof value === 'string') {
        return value.toUpperCase().indexOf(languageMatcherFilter) > -1;
      }
      return false;
    });

    if (responseArr.some((val) => val)) return true;
    return false;
  });
};

export const getLanguageMatcherByIdentifierId: Selector<MLanguageMatcher[], [UUID, string]> =
  createSelector(
    [
      getIdentifier,
      (state: GlobalState): NormalizedResource<NormalizedIdentifier> => state.identifier.items,
      (state: GlobalState): NormalizedResource<MLanguageMatcher> =>
        state.identifier.stagingIdentifier.languageMatchers,
      (state: GlobalState): NormalizedResource<MLanguageMatcher> =>
        state.identifier.productionIdentifier.languageMatchers,
      (state: GlobalState): string => state.identifier.selectedCustomerGroup,
      (_state: GlobalState, identifierId): string => identifierId,
      (_state: GlobalState, _identifierId, languageMatcherFilter): string => languageMatcherFilter,
    ],
    (
      identifier,
      identifiers,
      stagingMatchers,
      productionMatchers,
      customerGroup,
      identifierId,
      languageMatcherFilter
    ) => {
      let identifierMatchers = stagingMatchers;
      if (customerGroup === 'production') {
        identifierMatchers = productionMatchers;
      }

      const languageMatchers: MLanguageMatcher[] = [];
      if (!identifierId || !Object.keys(identifiers).length) {
        return languageMatchers;
      }

      if (!identifier) {
        return languageMatchers;
      }

      identifier.language_matchers.forEach((key) => {
        const languageMatcher = identifierMatchers[key];
        if (languageMatcher) {
          languageMatchers.push(languageMatcher);
        }
      });

      return filterLanguageMatchers(languageMatchers, languageMatcherFilter);
    }
  );

export const getLanguageMatcherFromSelectedIdentifier: Selector<
  MLanguageMatcher[],
  [string, boolean]
> = createSelector(
  [
    getSelectedIdentifierRevision,
    getIdentifierCustomers,
    (state: GlobalState): NormalizedResource<MLanguageMatcher> =>
      state.identifier.stagingIdentifier.languageMatchers,
    (state: GlobalState): NormalizedResource<MLanguageMatcher> =>
      state.identifier.productionIdentifier.languageMatchers,
    (state: GlobalState): string => state.identifier.selectedCustomerGroup,
    (_state: GlobalState, languageMatcherFilter): string => languageMatcherFilter,
    (_state: GlobalState, _languageMatcherFilter, shouldSort): string => shouldSort,
  ],
  (
    identifier,
    customers,
    stagingMatchers,
    productionMatchers,
    customerGroup,
    languageMatcherFilter,
    shouldSort
  ) => {
    let identifierMatchers = stagingMatchers;
    if (customerGroup === 'production') {
      identifierMatchers = productionMatchers;
    }

    const languageMatchers: MLanguageMatcher[] = [];

    if (!identifier) {
      return languageMatchers;
    }

    identifier.language_matchers.forEach((key) => {
      const languageMatcher = identifierMatchers[key];
      if (languageMatcher) {
        languageMatchers.push(languageMatcher);
      }
    });

    if (shouldSort) {
      return filterLanguageMatchers(languageMatchers, languageMatcherFilter).sort((a, b) => {
        if (a.is_exclusion && !b.is_exclusion) {
          return 1;
        }

        if (b.is_exclusion && !a.is_exclusion) {
          return -1;
        }

        const scopedA =
          a.customer_uuids?.filter((c) => customers.find((cu) => cu.uuid === c)) || [];
        const scopedB =
          b.customer_uuids?.filter((c) => customers.find((cu) => cu.uuid === c)) || [];

        if (scopedA.length > scopedB.length) {
          return -1;
        }

        return 1;
      });
    }

    return filterLanguageMatchers(languageMatchers, languageMatcherFilter);
  }
);

export const getStagingLanguageMatcher: Selector<MLanguageMatcher | null, [UUID]> = (
  state,
  languageMatcherId
) => {
  if (
    !languageMatcherId ||
    !Object.keys(state.identifier.stagingIdentifier.languageMatchers).length
  ) {
    return null;
  }

  return state.identifier.stagingIdentifier.languageMatchers[languageMatcherId];
};

export const getProductionLanguageMatcher: Selector<MLanguageMatcher | null, [UUID]> = (
  state,
  languageMatcherId
) => {
  if (
    !languageMatcherId ||
    !Object.keys(state.identifier.productionIdentifier.languageMatchers).length
  ) {
    return null;
  }

  return state.identifier.productionIdentifier.languageMatchers[languageMatcherId];
};

export const getStagingLanguageMatchers: Selector<MLanguageMatcher[]> = createSelector(
  [
    (state: GlobalState): GlobalState['identifier']['stagingIdentifier']['languageMatchers'] =>
      state.identifier.stagingIdentifier.languageMatchers,
  ],
  (languageMatchers) => Object.values(languageMatchers)
);

export const getProductionLanguageMatchers: Selector<MLanguageMatcher[]> = createSelector(
  [
    (state: GlobalState): GlobalState['identifier']['productionIdentifier']['languageMatchers'] =>
      state.identifier.productionIdentifier.languageMatchers,
  ],
  (languageMatchers) => Object.values(languageMatchers)
);

export const getProductionIdentifierRevision: Selector<NormalizedIdentifier | null> = (state) =>
  state.identifier.productionIdentifier.identifierRevision;

export const getActiveLanguageMatcher: Selector<MLanguageMatcher> = (state) => {
  let matchers = state.identifier.stagingIdentifier.languageMatchers;
  if (state.identifier.selectedCustomerGroup === 'production') {
    matchers = state.identifier.productionIdentifier.languageMatchers;
  }
  return matchers[state.identifier.activeLanguageMatcher];
};

export const getPromptBlockMove: Selector<{ up: boolean; down: boolean }> = createSelector(
  [
    (state: GlobalState): NormalizedResource<MLanguageMatcher> =>
      state.identifier.stagingIdentifier.languageMatchers,
    (state: GlobalState): NormalizedResource<MLanguageMatcher> =>
      state.identifier.productionIdentifier.languageMatchers,

    (state: GlobalState): string[] =>
      state.identifier.stagingIdentifier.identifierRevision?.language_matchers || [],
    (state: GlobalState): string[] =>
      state.identifier.productionIdentifier.identifierRevision?.language_matchers || [],

    (state: GlobalState): string => state.identifier.activeLanguageMatcher,
    (state: GlobalState): string => state.identifier.selectedCustomerGroup,
  ],
  (
    stagingMatchers,
    productionMatchers,
    stagingIds,
    productionIds,
    activeLanguageMatcherId,
    selectedCustomerGroup
  ) => {
    const move = { up: false, down: false };

    let matchers = stagingMatchers;
    let matcherIds = stagingIds;

    if (selectedCustomerGroup === 'production') {
      matchers = productionMatchers;
      matcherIds = productionIds;
    }

    const matcher = matchers[activeLanguageMatcherId];

    let filteredMatchers = Object.values(matchers);

    if (matcher.parent) {
      filteredMatchers = Object.values(matchers).filter((m) => m.parent === matcher.parent);
    } else {
      filteredMatchers = Object.values(matchers).filter((m) => !m.parent);
    }

    const filteredIds = matcherIds.filter((m) => filteredMatchers.find((fm) => fm.uuid === m));

    const matcherIndex = filteredIds.findIndex((m) => matcher.uuid === m);

    if (matcherIndex !== 0) {
      move.up = true;
    }
    if (matcherIndex !== filteredIds.length - 1) {
      move.down = true;
    }

    return move;
  }
);

export const getOriginalActiveLanguageMatcher: Selector<MLanguageMatcher | undefined> = (state) => {
  const matcher =
    state.identifier.productionIdentifier?.languageMatchers[state.identifier.activeLanguageMatcher];
  return matcher;
};

export const getActiveLanguageMatcherId: Selector<string> = (state) =>
  state.identifier.activeLanguageMatcher;

export const getLanguageMatcherTypes: Selector<LanguageMatchersTypes> = (state) =>
  state.identifier.languageMatcherTypes;

export const getRelationshipTypes: Selector<NormalizedRelationshipType[]> = (state) =>
  state.identifier.relationshipTypes;

export const getFetchRelationshipTypesLoading: Selector<boolean> = (state) =>
  state.identifier.loading.includes(fetchRelationshipTypesRequest.toString());

export const getIdentifiersTotalCount: Selector<number> = (state) => state.identifier.count;

export const getGeneratedTerms: Selector<string[]> = (state) =>
  state.identifier.generatedTerms[state.identifier.activeLanguageMatcher] || [];

export const getGeneratedTermsLoading: Selector<boolean> = (state) =>
  state.identifier.loading.includes(generateTermsRequest.toString());

export const getDropPosition: Selector<number> = (state) => state.identifier.dropPosition;

export const getCurrentTestIdentifierRevisionId: Selector<string> = (state) =>
  state.identifier.testIdentifierRevisionId;

export const getIdentifierShowUtilization: Selector<boolean> = (state) =>
  state.identifier.showUtilization;

export const getShowEditIdentifier: Selector<boolean> = (state) =>
  state.identifier.showEditIdentifier;

// FIXME: keyof LanguageMatcher should be dependent of the LanguageMatcherType
export const getFormSpec: Selector<LanguageMatcherForm, [LanguageMatcherType, string, UUID]> =
  createSelector(
    [
      (state: GlobalState): LanguageMatchersTypes => state.identifier.languageMatcherTypes,
      (state: GlobalState, _type, _form, languageMatcherId): MLanguageMatcher | null =>
        getStagingLanguageMatcher(state, languageMatcherId),
      (state: GlobalState, _type, _form, languageMatcherId): MLanguageMatcher | null =>
        getProductionLanguageMatcher(state, languageMatcherId),
      (state: GlobalState): string => state.identifier.selectedCustomerGroup,
      (_state: GlobalState, type): LanguageMatcherType => type,
      (_state: GlobalState, _type, form): string => form,
    ],
    (matcherTypes, stagingMatcher, productionMatcher, customerGroup, type, form) => {
      let baseMatcher = stagingMatcher;
      let compareMatcher = productionMatcher;

      if (customerGroup === 'production') {
        baseMatcher = productionMatcher;
        compareMatcher = stagingMatcher;
      }

      const specForType = matcherTypes[type as keyof LanguageMatchersTypes] as Record<
        string,
        LanguageMatcherFieldSpec
      >;
      const inputItem = specForType[form];
      if (inputItem == null) {
        throw new Error(`Invalid form: ${form} for type: ${type}`);
      }

      const row = (m: MLanguageMatcher | null): LanguageMatcherForm['inputValue'] =>
        (m ? (m as Record<string, unknown>)[form] : null) as LanguageMatcherForm['inputValue'];
      const inputValue = row(baseMatcher);
      const originalValue = row(compareMatcher);
      const inputErrors =
        baseMatcher && 'errorFields' in baseMatcher ? baseMatcher.errorFields : null;
      const inputChoices =
        inputItem.choices != null ? mapInputChoices(inputItem.choices, form) : null;
      const inputNumbered = Boolean((inputItem as unknown as { numbered?: boolean })?.numbered);

      let inputType = possibleTypes.freeForm;
      if (inputChoices !== null) {
        if (inputItem.is_list === true) {
          inputType = possibleTypes.multiSelect;
        } else {
          inputType = possibleTypes.singleSelect;
        }
      } else if (inputItem.is_list === true) {
        inputType = possibleTypes.freeForm;
      } else if (inputItem.type === 'bool') {
        inputType = possibleTypes.bool;
      } else if (inputItem.type === 'int') {
        inputType = possibleTypes.int;
      }

      return {
        inputType,
        inputValue,
        originalValue,
        inputErrors,
        inputChoices,
        inputName: inputItem.name,
        inputRequired: inputItem.required,
        inputHidden: inputItem.hidden,
        inputNumbered,
      };
    }
  );

export const getSelectedMatcherScopesForUser: Selector<string[]> = createSelector(
  [getActiveLanguageMatcher, getIdentifierCustomers],
  (matcher, customers) => {
    if (matcher) {
      const scopedCustomers = matcher.customer_uuids || [];
      const scopedCustomersForUser = scopedCustomers.filter((c) =>
        customers.find((cu) => cu.uuid === c)
      );
      return scopedCustomersForUser;
    }
    return [];
  }
);

export const getOriginalMatcherUtilization: Selector<UtilizationType, [UUID]> = (state, itemId) => {
  const matcher = state.identifier.productionIdentifier?.languageMatchers[itemId];
  const scopedCustomers = matcher?.customer_uuids || [];
  const customers = getIdentifierCustomers(state);

  const scopedCustomersForUser = scopedCustomers.filter((c) =>
    customers.find((cu) => cu.uuid === c)
  );

  if (scopedCustomersForUser.length === customers.length) return 'all';
  if (
    scopedCustomersForUser.length >= customers.length / 2 &&
    scopedCustomersForUser.length < customers.length
  )
    return 'more-than-half';
  if (scopedCustomersForUser.length > 1 && scopedCustomersForUser.length < customers.length / 2)
    return 'less-than-half';
  if (scopedCustomersForUser.length === 1) return 'one';

  return 'none';
};

export const getMatcherUtilization: Selector<UtilizationType, [UUID]> = (state, itemId) => {
  const stagingMatcher = getStagingLanguageMatcher(state, itemId);
  const productionMatcher = getProductionLanguageMatcher(state, itemId);

  let matcher = stagingMatcher;

  if (state.identifier.selectedCustomerGroup === 'production') {
    matcher = productionMatcher;
  }
  const scopedCustomers = matcher?.customer_uuids || [];
  const customers = getIdentifierCustomers(state);

  const scopedCustomersForUser = scopedCustomers.filter((c) =>
    customers.find((cu) => cu.uuid === c)
  );

  if (scopedCustomersForUser.length === customers.length) return 'all';
  if (
    scopedCustomersForUser.length >= customers.length / 2 &&
    scopedCustomersForUser.length < customers.length
  )
    return 'more-than-half';
  if (scopedCustomersForUser.length > 1 && scopedCustomersForUser.length < customers.length / 2)
    return 'less-than-half';
  if (scopedCustomersForUser.length === 1) return 'one';

  return 'none';
};

export const getOriginalScopedCustomersMatcher: Selector<string[], [UUID]> = (state, id) => {
  const item = state.identifier.productionIdentifier?.languageMatchers[id];
  if (item && 'customer_uuids' in item) {
    return item.customer_uuids || [];
  }

  return [];
};

export const getScopedCustomersMatcher: Selector<string[], [UUID]> = (state, id) => {
  let item = state.identifier.stagingIdentifier.languageMatchers[id];
  if (state.identifier.selectedCustomerGroup === 'production') {
    item = state.identifier.productionIdentifier.languageMatchers[id];
  }
  if ('customer_uuids' in item) {
    return item.customer_uuids || [];
  }

  return [];
};

export const getFetchIdentifierCustomersLoading: Selector<boolean> = (state) =>
  state.identifier.loading.includes(fetchIdentifierCustomersRequest.toString());

export const getLanguageMatcherTermsSearchQuery: Selector<string> = (state) => {
  const languageMatchers = getLanguageMatcherFromSelectedIdentifier(state, '', false);

  const terms = languageMatchers
    .filter((l) => l.type === 'token')
    .reduce<string[]>((acc, curr) => {
      if (!curr.terms || curr.terms.length === 0) return acc;

      const arr = curr.terms.reduce<string[]>((ac, cu) => [...ac, cu], []);

      return [...acc, ...arr];
    }, []);

  const query = [...new Set(terms)].reduce((acc, curr, idx) => {
    if (idx === 0) {
      return `"${curr}"`;
    }
    return `${acc} | "${curr}"`;
  }, '');

  return query;
};

export const getChangedLanguageMatchers: Selector<string[]> = createSelector(
  [
    (state: GlobalState): NormalizedIdentifier | null =>
      state.identifier.stagingIdentifier.identifierRevision,
    getStagingLanguageMatchers,
    getProductionLanguageMatchers,
    (state: GlobalState): string => state.identifier.selectedCustomerGroup,
  ],
  (stagingIdentifier, stagingLanguageMatchers, productionLanguageMatchers, customerGroup) => {
    if (!stagingIdentifier || !stagingIdentifier.uuid) return [];

    let baseMatchers = stagingLanguageMatchers;
    let compareMatchers = productionLanguageMatchers;

    if (customerGroup === 'production') {
      baseMatchers = productionLanguageMatchers;
      compareMatchers = stagingLanguageMatchers;
    }

    const changed: string[] = [];

    baseMatchers.forEach((matcher) => {
      const originalMatcher = compareMatchers.find((m) => m.uuid === matcher.uuid);
      if (!originalMatcher) {
        changed.push(matcher.uuid);
        return;
      }

      Object.keys(matcher).forEach((key) => {
        if (
          key !== 'saved' &&
          !isEqual(
            matcher[key as keyof typeof matcher],
            originalMatcher[key as keyof typeof originalMatcher]
          )
        ) {
          changed.push(matcher.uuid);
        }
      });

      /* if (!isEqual(matcher, originalMatcher)) {
        changed.push(matcher.uuid);
      } */
    });

    return [...new Set(changed)];
  }
);

export const getChangedLanguageMatchersNames: Selector<string[]> = createSelector(
  [
    getChangedLanguageMatchers,
    getStagingLanguageMatchers,
    getProductionLanguageMatchers,
    (state: GlobalState): string => state.identifier.selectedCustomerGroup,
  ],
  (changedMatchers, stagingLanguageMatchers, productionLanguageMatchers, customerGroup) => {
    let baseMatchers = stagingLanguageMatchers;
    let compareMatchers = productionLanguageMatchers;

    if (customerGroup === 'production') {
      baseMatchers = productionLanguageMatchers;
      compareMatchers = stagingLanguageMatchers;
    }

    const names: string[] = [];

    changedMatchers.forEach((matcher) => {
      let foundMatcher = baseMatchers.find((m) => m.uuid === matcher);
      if (!foundMatcher) {
        foundMatcher = compareMatchers.find((m) => m.uuid === matcher);
      }
      if (foundMatcher) {
        names.push(foundMatcher.name);
      }
    });

    return names;
  }
);

export const getCompareMode: Selector<boolean> = (state) => state.identifier.compareMode;

export const getInProd: Selector<boolean> = (state) =>
  state.identifier.selectedCustomerGroup === 'production';

export const getCustomerGroup: Selector<string> = (state) => state.identifier.selectedCustomerGroup;

export const getHasStaging: Selector<boolean> = (state) =>
  !!state.identifier.stagingIdentifier.identifierRevision?.uuid;

export const getShouldDisableEdit: Selector<boolean> = (state) =>
  getInProd(state) && getHasStaging(state);

export const getPromptCompareMode: Selector<{
  isComparing: boolean;
  secondaryMatcher: string;
}> = (state) => state.identifier.promptCompare;

export const getSecondaryMatcher: Selector<MLanguageMatcher> = (state) => {
  let matchers = state.identifier.stagingIdentifier.languageMatchers;
  if (state.identifier.selectedCustomerGroup === 'production') {
    matchers = state.identifier.productionIdentifier.languageMatchers;
  }
  return matchers[state.identifier.promptCompare.secondaryMatcher];
};

export const getCustomersMissingThresholds: Selector<string[]> = createSelector(
  [
    (state: GlobalState): GlobalState['identifier']['stagingIdentifier'] =>
      state.identifier.stagingIdentifier,
    (state: GlobalState): GlobalState['identifier']['productionIdentifier'] =>
      state.identifier.productionIdentifier,
    (state: GlobalState): GlobalState['identifier']['selectedCustomerGroup'] =>
      state.identifier.selectedCustomerGroup,
  ],
  (stagingIdentifier, productionIdentifier, selectedCustomerGroup) => {
    let identifier = stagingIdentifier.identifierRevision;
    let categories = stagingIdentifier.identifierRevision?.options_config
      .categorization_facets as string[];
    let customers = stagingIdentifier.identifierCustomers;

    if (selectedCustomerGroup === 'production') {
      identifier = productionIdentifier.identifierRevision;
      categories = productionIdentifier.identifierRevision?.options_config
        .categorization_facets as string[];
      customers = productionIdentifier.identifierCustomers;
    }

    if (categories.length === 0) return [];

    const customersMissingThresholds: string[] = [];

    customers.forEach((customer) => {
      // @ts-ignore
      const thresholds = identifier?.options_config.customer_thresholds?.[customer.uuid];
      if (!thresholds) {
        customersMissingThresholds.push(customer.uuid);
      } else if (categories.some((cat) => !thresholds[cat])) {
        customersMissingThresholds.push(customer.uuid);
      }
    });

    return customersMissingThresholds;
  }
);

export const getCustomersMissingVariables: Selector<string[]> = createSelector(
  [
    (state: GlobalState): GlobalState['identifier']['stagingIdentifier'] =>
      state.identifier.stagingIdentifier,
    (state: GlobalState): GlobalState['identifier']['productionIdentifier'] =>
      state.identifier.productionIdentifier,
    (state: GlobalState): GlobalState['identifier']['selectedCustomerGroup'] =>
      state.identifier.selectedCustomerGroup,
  ],
  (stagingIdentifier, productionIdentifier, selectedCustomerGroup) => {
    let identifier = stagingIdentifier.identifierRevision;
    let variables = stagingIdentifier.identifierRevision?.options_config.variables as string[];
    let customers = stagingIdentifier.identifierCustomers;

    if (selectedCustomerGroup === 'production') {
      identifier = productionIdentifier.identifierRevision;
      variables = productionIdentifier.identifierRevision?.options_config.variables as string[];
      customers = productionIdentifier.identifierCustomers;
    }

    if (!variables || variables.length === 0) return [];

    const customersMissingVariables: string[] = [];

    customers.forEach((customer) => {
      // @ts-ignore
      const customerVariables = identifier?.options_config.customers_variables?.[customer.uuid];
      if (!customerVariables) {
        customersMissingVariables.push(customer.uuid);
      } else if (variables.some((v) => !customerVariables[v])) {
        customersMissingVariables.push(customer.uuid);
      }
    });

    return customersMissingVariables;
  }
);

export const getRenderedPromptLoading: Selector<boolean> = (state) =>
  state.identifier.loading.includes(fetchRenderedPromptsRequest.toString());

export const getRenderedPrompt: Selector<string> = (state) => {
  const { selectedCustomerGroup } = state.identifier;

  if (selectedCustomerGroup === 'staging') {
    return state.identifier.stagingIdentifier.renderedPrompt;
  }
  return state.identifier.productionIdentifier.renderedPrompt;
};
